import FW from './fw';
import Env from './env';
import Logger from './logger';

export default class License {

  #rmp;
  #fileProtocol = false;
  #isLocal = false;
  #isLicValid = 'false';
  #licType = 'rmp';
  #licUrl = '';
  #hostname = null;
  #siteCheckerRetryTimeout = null;
  #rmpPlusCheckerRetryTimeout = null;
  #platformArrayList = [];
  #platformAppArrayList = [];
  #platformListType = '';
  #vgsUri = 'radiantmediatechs.com/rmp/rr/vgs-o.json';
  #vgtUri = 'radiantmediatechs.com/rmp/rr/vgt-o.json';
  #vgrpUri = 'radiantmediatechs.com/rmp/rr/vgrp-o.json';

  constructor(rmp) {
    this.#rmp = rmp;
    if (typeof window.rmpGetHostname === 'string') {
      this.#hostname = window.rmpGetHostname;
    } else if (typeof window.location !== 'undefined' && window.location.hostname) {
      this.#hostname = window.location.hostname;
    }
    if (this.#hostname) {
      this.#hostname = this.#hostname.toLowerCase().replace(/^www\./, '');
    }
  }

  get hostname() {
    return this.#hostname;
  }

  get type() {
    return this.#licType;
  }

  static isTrial(decodedKey) {
    // is Trial
    let expire;
    if (decodedKey.indexOf('!') > -1) {
      expire = decodedKey.split('!');
      const trialChars = {
        s: 0,
        c: 1,
        o: 2,
        g: 3,
        i: 4,
        k: 5,
        m: 6,
        e: 7,
        q: 8,
        a: 9
      };
      const decodedExpire = expire[0].replace(/[scogikmeqa]/g, item => {
        return trialChars[item];
      });
      // check that all decodedExpire are numbers to avoid hacking
      // if not trial has expired and should return
      const pattern = /^\d+$/;
      if (pattern.test(decodedExpire)) {
        const expireDate = parseInt(decodedExpire, 10);
        let currentDate = new Date();
        const time = currentDate.getTime();
        currentDate = Math.round(time / 1000);
        const delta = 16 * 24 * 60 * 60;
        if (expireDate > currentDate + delta) {
          // to avoid hacking of expiration date
          return [true, expire[1], 'expired'];
        } else if (currentDate > expireDate) {
          return [true, expire[1], 'expired'];
        } else {
          return [true, expire[1], 'notexpired'];
        }
      } else {
        return [true, expire[1], 'expired'];
      }
    }
    return [false, null, 'notexpired'];
  }

  static isLocalTest(testSite) {
    if (!testSite) {
      return false;
    }
    const testPattern = /^(site.rmp|localhost|bs-local.com)/i;
    if (testPattern.test(testSite)) {
      return true;
    }
    const ipv4Pattern = /^\d+\.\d+\.\d+\.\d+$/i;
    if (ipv4Pattern.test(testSite)) {
      const ipPattern1 = /^192\.168\./i;
      const ipPattern2 = /^172\.[1-3]/i;
      const ipPattern3 = /^10\./i;
      const altLocal = '127.0.0.1';
      if (testSite === altLocal || ipPattern1.test(testSite) ||
        ipPattern2.test(testSite) || ipPattern3.test(testSite)) {
        return true;
      }
    }
    return false;
  }

  #onRmpPromiseResolved(data) {
    // decode base64 data in JSON file
    let jsonData;
    this.#isLicValid = 'true';
    try {
      jsonData = (JSON.parse(data)).data;
      jsonData = jsonData.replace(/---------------------/g, '').split('').reverse().join('');
      jsonData = atob(jsonData);
      jsonData = JSON.parse(jsonData);
      if (DEBUG) {
        if (this.#rmp.debugLevel === 777) {
          Logger.print('debug-more', 'decodedData follows: ', jsonData);
        }
      }
    } catch (warning) {
      console.warn(warning);
      return;
    }
    if (jsonData && jsonData.sites && jsonData.permanentBL && this.#hostname) {
      let isBL = false;
      for (let i = 0, len = jsonData.permanentBL.length; i < len; i++) {
        const blSite = jsonData.permanentBL[i];
        if (this.#hostname.indexOf(blSite) > -1) {
          isBL = true;
          break;
        }
      }
      if (isBL) {
        if (DEBUG) {
          if (this.#rmp.debugLevel === 777) {
            Logger.print('debug-more', 'permanentBL');
          }
        }
        this.terminate(this.#licType.toUpperCase() + ' Edition auth failed');
        this.#isLicValid = 'false';
      } else {
        const sites = jsonData.sites;
        for (let i = 0, len = sites.length; i < len; i++) {
          if (this.#hostname.indexOf(sites[i]) > -1) {
            // double check for example b-stream.com fails if stream.com is in the list
            const sitesOnJson = sites[i];
            const splitSitesOnJson = sitesOnJson.split('.');
            const lastTwo = splitSitesOnJson[splitSitesOnJson.length - 2] + '.' + splitSitesOnJson[splitSitesOnJson.length - 1];
            if (lastTwo.indexOf(this.#hostname) > -1) {
              this.terminate(this.#licType.toUpperCase() + ' Edition auth failed');
              this.#isLicValid = 'false';
              break;
            }
          }
        }
      }
    }
    if (DEBUG) {
      if (this.#rmp.debugLevel === 777) {
        Logger.print('debug-more', 'license key ' + this.#rmp.licenseKey + ' is' + (this.#isLicValid === 'false' ? ' NOT' : '') + ' valid for ' + this.#hostname);
      }
    }
  }

  #siteChecker(url) {
    this.#licUrl = url;
    const onRmpPromiseResolved = this.#onRmpPromiseResolved.bind(this);
    FW.ajax(this.#licUrl, false, 'GET').
      then(onRmpPromiseResolved).
      catch(warning => {
        console.warn(warning);
        window.clearTimeout(this.#siteCheckerRetryTimeout);
        this.#siteCheckerRetryTimeout = window.setTimeout(() => {
          FW.ajax(this.#licUrl, false, 'GET').
            then(onRmpPromiseResolved).
            catch(warning2 => {
              console.warn(warning2);
            });
        }, 10000);
      });
  }

  #checkForCustomList(customer) {
    if (!this.#fileProtocol) {
      if (Array.isArray(customer.blacklist) && customer.blacklist.length > 0) {
        this.#platformArrayList = customer.blacklist;
        this.#platformListType = 'blacklist';
      } else if (Array.isArray(customer.whitelist) && customer.whitelist.length > 0) {
        this.#platformArrayList = customer.whitelist;
        this.#platformListType = 'whitelist';
      }
    } else {
      if (Array.isArray(customer.appWhitelist) && customer.appWhitelist.length > 0) {
        this.#platformAppArrayList = customer.appWhitelist;
      }
    }
  }

  #postCustomListCheck() {
    if (DEBUG) {
      if (this.#rmp.debugLevel === 777) {
        Logger.print('debug-more', 'platformArrayList:', this.#platformArrayList);
        Logger.print('debug-more', 'platformAppArrayList:', this.#platformAppArrayList);
        Logger.print('debug-more', 'platformListType: ' + this.#platformListType);
      }
    }
    if (this.#isLicValid === 'false') {
      this.terminate('PLATFORM Edition auth failed');
      return;
    } else {
      if (this.#isLocal) {
        this.#isLicValid = 'true';
        return;
      }
      if (!this.#fileProtocol && this.#platformArrayList.length > 0) {
        if (this.#platformListType === 'blacklist') {
          this.#isLicValid = 'true';
          for (let i = 0, len = this.#platformArrayList.length; i < len; i++) {
            if (this.#hostname && this.#hostname.indexOf(this.#platformArrayList[i]) > -1) {
              this.#isLicValid = 'false';
              break;
            }
          }
        } else if (this.#platformListType === 'whitelist') {
          this.#isLicValid = 'false';
          for (let i = 0, len = this.#platformArrayList.length; i < len; i++) {
            if (this.#hostname && this.#hostname.indexOf(this.#platformArrayList[i]) > -1) {
              this.#isLicValid = 'true';
              break;
            }
          }
        }
      } else if (this.#fileProtocol && this.#platformAppArrayList.length > 0 &&
        typeof this.#platformAppArrayList[0] === 'string' && this.#platformAppArrayList[0] !== '') {
        const appName = this.#rmp.dom.container.getAttribute('data-rwlan');
        if (appName) {
          this.#isLicValid = 'false';
          let decodedAppName;
          try {
            decodedAppName = atob(appName);
          } catch (warning) {
            decodedAppName = null;
            console.warn(warning);
          }
          if (decodedAppName) {
            for (let i = 0, len = this.#platformAppArrayList.length; i < len; i++) {
              if (decodedAppName.indexOf(this.#platformAppArrayList[i]) > -1) {
                this.#isLicValid = 'true';
                break;
              }
            }
          }
        } else {
          // there is a this.#platformAppArrayList but no data-rwlan - this is not valid
          this.#isLicValid = 'false';
        }
      }
    }
    if (this.#isLicValid === 'false') {
      this.terminate('PLATFORM Edition auth failed');
    }
  }

  #getCustomList(customer) {
    if (this.#rmp.customAllowDenyListUrl !== '') {
      if (DEBUG) {
        if (this.#rmp.debugLevel === 777) {
          Logger.print('debug-more', 'Default Allow Deny List: ', JSON.parse(JSON.stringify(customer)));
        }
      }
      FW.ajax(this.#rmp.customAllowDenyListUrl, false, 'GET').
        then(data => {
          let jsonData;
          try {
            jsonData = JSON.parse(data);
          } catch (warning) {
            console.warn(warning);
            this.#checkForCustomList(customer);
            this.#postCustomListCheck();
            return;
          }
          if (jsonData.allowlist) {
            customer.whitelist = jsonData.allowlist;
          }
          if (jsonData.denylist) {
            customer.blacklist = jsonData.denylist;
          }
          if (jsonData.appAllowlist) {
            customer.appWhitelist = jsonData.appAllowlist;
          }
          if (DEBUG) {
            if (this.#rmp.debugLevel === 777) {
              Logger.print('debug-more', 'Custom Allow Deny List: ', customer);
            }
          }
          this.#checkForCustomList(customer);
          this.#postCustomListCheck();
        }).catch(warning => {
          console.warn(warning);
          this.#checkForCustomList(customer);
          this.#postCustomListCheck();
        });
    } else {
      this.#checkForCustomList(customer);
    }
  }

  #onPlatformPromiseResolved(data) {
    let jsonData;
    try {
      jsonData = (JSON.parse(data)).data;
      jsonData = jsonData.replace(/---------------------/g, '').split('').reverse().join('');
      jsonData = atob(jsonData);
      jsonData = JSON.parse(jsonData);
      if (DEBUG) {
        if (this.#rmp.debugLevel === 777) {
          Logger.print('debug-more', 'decodedData follows: ', jsonData);
        }
      }
    } catch (warning) {
      console.warn(warning);
      return;
    }
    if (jsonData && jsonData.sites && jsonData.permanentBL) {
      let isBL = false;
      for (let i = 0, len = jsonData.permanentBL.length; i < len; i++) {
        const blSite = jsonData.permanentBL[i];
        if (this.#hostname && this.#hostname.indexOf(blSite) > -1) {
          isBL = true;
          break;
        }
      }
      if (isBL) {
        if (DEBUG) {
          if (this.#rmp.debugLevel === 777) {
            Logger.print('debug-more', 'permanentBL');
          }
        }
        this.#isLicValid = 'false';
      } else {
        const sites = jsonData.sites;
        for (let i = 0, len = sites.length; i < len; i++) {
          if (sites[i].license) {
            let license = sites[i].license;
            license = license.split('').reverse().join('');
            if (license === this.#rmp.licenseKey) {
              this.#isLicValid = 'true';
              this.#getCustomList(sites[i]);
              break;
            }
          }
        }
      }
    }
    if (this.#rmp.customAllowDenyListUrl === '') {
      this.#postCustomListCheck();
    }
  }

  #rmpPlusChecker(url) {
    this.#licUrl = url;
    FW.ajax(this.#licUrl, false, 'GET').
      then(this.#onPlatformPromiseResolved.bind(this)).
      catch(warning => {
        console.warn(warning);
        window.clearTimeout(this.#rmpPlusCheckerRetryTimeout);
        this.#rmpPlusCheckerRetryTimeout = window.setTimeout(() => {
          FW.ajax(this.#licUrl, false, 'GET').
            then(this.#onPlatformPromiseResolved.bind(this)).
            catch(warning2 => {
              console.warn(warning2);
            });
        }, 10000);
      });
  }

  #isValidKey(key, testSite) {
    const uriLicense = /radiantmediatechs\.com\/rmp\/rr\/vg(s-o|t-o|rp-o)\.json/i;
    if (!uriLicense.test(this.#vgsUri) || !uriLicense.test(this.#vgtUri) || !uriLicense.test(this.#vgrpUri)) {
      if (DEBUG) {
        if (this.#rmp.debugLevel === 777) {
          Logger.print('debug-more', 'fraud on license servers detected');
        }
      }
      return 0;
    }
    if (this.#rmp.strictGdprCompliance) {
      this.#vgsUri = 'https://cdn-gdpr.' + this.#vgsUri;
      this.#vgtUri = 'https://cdn-gdpr.' + this.#vgtUri;
      this.#vgrpUri = 'https://cdn-gdpr.' + this.#vgrpUri;
    } else {
      this.#vgsUri = 'https://cdn.' + this.#vgsUri;
      this.#vgtUri = 'https://cdn.' + this.#vgtUri;
      this.#vgrpUri = 'https://cdn.' + this.#vgrpUri;
    }
    let decodedKey;
    try {
      decodedKey = atob(key);
    } catch (warning) {
      console.warn(warning);
      return 0;
    }
    // check for blacklist
    const blacklistPattern = /(qqtv\.|freestreams-live1.com|redditsport.live|mgstrm.website|lionsdelatlas.ma|digi-hdsport.com|fomny.com|streams.rip|brstreams.xyz|liluka-site.eu|epgssiptv.tk|mygoodstream.pw|elahmad.com|hdtvku.com|sportstreamshd.com|mygoaltv.com|trgooltv5.co|tvpoztmo.com|cialis99.com|adreach.co|rumahtv.com|mygstrm748463.tk|nhl.ru|diag-auto.site|onhockey.tv|purakuraplay.com|dailysport.club|minifreeza.eu|megatelevisionhd.tk|tvlatinohd.ml|megatelehd.tk|megamundotv.tk|cms.e.jimdo.com|portenavirtualestereo.jimdofree.com|cambelltv.blogspot.com|spl.sa|koora-shoot.online)/i;
    if (this.#hostname && blacklistPattern.test(this.#hostname)) {
      if (DEBUG) {
        if (this.#rmp.debugLevel === 777) {
          Logger.print('debug-more', 'domain ' + this.#hostname + ' is marked as disabled because of terms of service infringement - exit player setup');
        }
      }
      return 0;
    }
    // check for protocol/localhost
    if (Env.protocol === 'file:' || Env.protocol === 'ms-appx-web:' || typeof window.Capacitor !== 'undefined' || typeof window.cordova !== 'undefined') {
      this.#fileProtocol = true;
    } else {
      this.#isLocal = License.isLocalTest(testSite);
    }
    // check for PLATFORM Edition
    const platformPattern = /^\D{10}@\d+$/i;
    if (platformPattern.test(decodedKey)) {
      this.#licType = 'plus';
      this.#rmpPlusChecker(this.#vgrpUri);
      return 3;
    }
    const rmpPattern = /\*_%.+/i;
    if (!rmpPattern.test(decodedKey)) {
      return 0;
    }
    decodedKey = decodedKey.replace('*_%', '');
    decodedKey = decodedKey.replace('%_*', '');
    // is R3
    let r3 = false;
    if (decodedKey.indexOf('?') > -1) {
      const split = decodedKey.split('?');
      if (split[1] === 'rom5dasis30db0A') {
        r3 = true;
        decodedKey = decodedKey.replace('?rom5dasis30db0A', '');
      }
    }
    // is Trial
    const isTrial = License.isTrial(decodedKey);
    const trial = isTrial[0];
    if (trial) {
      this.#licType = 'trial';
      decodedKey = isTrial[1];
      if (isTrial[2] === 'expired') {
        Logger.print('license', 'trial has expired');
        return 0;
      }
    }
    // if RMP Edition and not trial 
    // file protocol is not allowed
    if (this.#fileProtocol && !trial) {
      return 0;
    }
    // isLocal
    if (this.#isLocal) {
      if (r3 || trial) {
        return 2;
      }
      if (!r3) {
        return 1;
      }
    }
    // decode key
    const _validChars = {
      '=': 'a',
      '+': 'b',
      'y': 'c',
      'z': 'd',
      'v': 'e',
      'u': 'f',
      'd': 'g',
      'b': 'h',
      's': 'i',
      'q': 'j',
      'a': 'k',
      'c': 'l',
      'i': 'm',
      'k': 'n',
      'e': 'o',
      'g': 'p',
      'n': 'q',
      'o': 'r',
      '0': 's',
      '7': 't',
      '8': 'u',
      '9': 'v',
      '4': 'w',
      '5': 'x',
      '6': 'y',
      '1': 'z',
      '2': '.',
      '3': '-'
    };
    const decodedSite = decodedKey.replace(/[=+yzvudbsqacikegno0789456123]/g, c => {
      return _validChars[c];
    });
    // check against JSON
    if (trial) {
      if (!this.#fileProtocol) {
        this.#siteChecker(this.#vgtUri, decodedSite);
      }
    } else {
      this.#siteChecker(this.#vgsUri, decodedSite);
    }
    let validSite = false;
    if (this.#fileProtocol) {
      validSite = true;
    } else if (testSite) {
      const site = testSite.replace(/\d/g, '');
      if (site.indexOf(decodedSite) > -1) {
        validSite = true;
      }
    }
    if ((trial || r3) && validSite) {
      return 2;
    }
    if (!r3 && !trial && validSite) {
      return 1;
    }
    return 0;
  }

  destroy() {
    window.clearTimeout(this.#siteCheckerRetryTimeout);
    window.clearTimeout(this.#rmpPlusCheckerRetryTimeout);
  }

  terminate(endType) {
    this.#rmp.rmpReadyManagement.minimalFire();
    this.#rmp.one('destroycompleted', () => {
      const container = document.getElementById(this.#rmp.id);
      if (container) {
        FW.removeElement(container);
      }
    });
    Logger.print('error', endType);
    this.#rmp.rmpUtils.error(104, null, true);
    this.#rmp.destroy().catch(error => {
      this.#rmp.rmpUtils.warning(1022, error);
    });
    this.#rmp.rejectPromise('init', this.#rmp.errorData);
  }

  validate() {
    if (DEBUG) {
      if (this.#rmp.debugLevel === 777) {
        Logger.print('debug-more', 'hosting domain is ' + this.#hostname);
      }
    }
    if (Env.isInIframe && this.#hostname === null && document.referrer) {
      try {
        this.#hostname = (new URL(document.referrer).hostname).toLowerCase().replace(/^www\./, '');
      } catch (warning) {
        console.warn(warning);
      }
    }
    const key = this.#isValidKey(this.#rmp.licenseKey, this.#hostname);
    // key = 1 / R2, key = 2 / R3, key = 0 / invalid
    if (key === 1 || key === 2 || key === 3) {
      return true;
    }
    return false;
  }

}
